// Vendors
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Actions
import {
  setUsername,
  setPassword,
  signIn,
} from '../../../reducers/auth/authActions';

// Selectors
import {
  selectUsername,
  selectPassword,
  selectIsLoading,
  selectSignInServerError,
  selectAppName,
  selectClientId,
  selectAppUrl,
  selectFieldErrors,
  selectUtmParams,
} from '../../../reducers/auth/authSelectors';
import { selectSelectedLanguage } from '../../../reducers/language/languageSelectors';
import {
  validateEmail,
  validatePassword,
} from '../../../lib/authValidators';
import { buildUrl } from '../../../lib/utmHelpers';
import {
  createAccountUrl,
  learnMoreUrl,
  learnMoreUrlEs,
} from '../../../constants/appUrls';

// Styles
import './sign-in-form.scss';

// Components
import {
  TxButton,
  TxButtonMode,
  TxAlert,
  TxAlertType,
  TxInput,
} from 'texkit-ui';
import { Trans } from '../../../components/Trans';
import Icon from '../../../components/Icon/Icon';
import WhatsWrong from '../WhatsWrong/WhatsWrong';

const SignInForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ email: '', password: '' });
  const [touched, setTouched] = useState({ email: false, password: false });
  const [showWhatsWrong, setShowWhatsWrong] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const username = useSelector(selectUsername);
  const password = useSelector(selectPassword);
  const isLoading = useSelector(selectIsLoading);
  const error = useSelector(selectSignInServerError);
  const serverFieldErrors = useSelector(selectFieldErrors);
  const appName = useSelector(selectAppName);
  const clientId = useSelector(selectClientId);
  const appUrl = useSelector(selectAppUrl);
  const locale = useSelector(selectSelectedLanguage);
  const utmParams = useSelector(selectUtmParams);
  const txtLearnMore = locale === 'es' ? learnMoreUrlEs : learnMoreUrl;
  
  useEffect(() => {
    if (serverFieldErrors.email || serverFieldErrors.password) {
      setFieldErrors(prev => ({
        ...prev,
        ...serverFieldErrors,
      }));
    }
  }, [serverFieldErrors]);
  
  const hasEmailValue = Boolean(username);
  const hasPasswordValue = Boolean(password);

  const langParam = `lang=${locale}`;

  const createAccountUrlWithParams = (clientId && appUrl && appName)
    ? `${buildUrl(`${createAccountUrl}?${langParam}`, utmParams)}&${new URLSearchParams({ client_id: clientId, app_url: appUrl, app_name: appName })}`
    : buildUrl(`${createAccountUrl}?${langParam}`, utmParams);

  const validateFields = () => ({
    email: validateEmail(username),
    password: validatePassword(password),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTouched({ email: true, password: true });

    const nextErrors = validateFields();
    setFieldErrors(nextErrors);

    if (nextErrors.email || nextErrors.password) {
      return;
    }

    const result = await dispatch(signIn({ username, password }));
    
    if (result?.success && result?.showGrantPermission) {
      navigate('/verify-consent', { replace: true });
    }
  };

  const handleUsernameChange = value => {
    dispatch(setUsername(value));
    if (touched.email) {
      setFieldErrors(prev => ({ ...prev, email: validateEmail(value) }));
    }
  };

  const handlePasswordChange = value => {
    dispatch(setPassword(value));
    if (touched.password) {
      setFieldErrors(prev => ({ ...prev, password: validatePassword(value) }));
    }
  };

  const handleFieldBlur = field => {
    setTouched(prev => ({ ...prev, [field]: true }));

    if (field === 'email') {
      setFieldErrors(prev => ({ ...prev, email: validateEmail(username) }));
      return;
    }

    setFieldErrors(prev => ({ ...prev, password: validatePassword(password) }));
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const handleOpenCreateAccount = () => {
    window.open(createAccountUrlWithParams, '_blank', 'noopener,noreferrer');
  };

  const handleOpenLearnMore = () => {
    window.open(buildUrl(txtLearnMore, utmParams), '_blank', 'noopener,noreferrer');
  };

  // If showing "What's Wrong" view, render that instead
  if (showWhatsWrong) {
    return <WhatsWrong onBack={() => setShowWhatsWrong(false)} />;
  }

  return (
    <div className="step sign-in-step">
      <h1 className="step-title">
        <Trans file="auth" id="signInTitle" />
      </h1>
      <p className="step-subtitle">
        <Trans file="auth" id="signInSubtitlePrefix" />
        <strong>{appName}</strong>
      </p>

      <form className="step-form sign-in-step-form" onSubmit={handleSubmit} noValidate>
        <div className={`form-field ${fieldErrors.email ? 'form-field-error' : ''} ${hasEmailValue ? 'form-field-has-value' : ''}`}>
          <div className="form-field-frame">
            <label className="form-field-label" htmlFor="email-input">
              <Trans file="auth" id="signInEmailLabel" />
            </label>
            <div className="form-field-input-row">
              <input
                id="email-input"
                className="form-input-inline"
                type="text"
                placeholder={t('signInEmailPlaceholder')}
                value={username}
                onChange={e => handleUsernameChange(e.target.value)}
                onBlur={() => handleFieldBlur('email')}
                autoComplete="username"
                aria-invalid={Boolean(fieldErrors.email)}
              />
            </div>
          </div>
          {fieldErrors.email && (
            <div className="field-inline-error" role="alert">
              <p className="field-inline-error-message">{t(fieldErrors.email)}</p>
            </div>
          )}
        </div>

        <div className={`form-field ${fieldErrors.password ? 'form-field-error' : ''} ${hasPasswordValue ? 'form-field-has-value' : ''}`}>
          <div className="form-field-frame">
            <label className="form-field-label" htmlFor="password-input">
              <Trans file="auth" id="signInPasswordLabel" />
            </label>
            <div className="form-field-input-row">
              <input
                id="password-input"
                className="form-input-inline"
                type={showPassword ? 'text' : 'password'}
                placeholder={t('signInPasswordPlaceholder')}
                value={password}
                onChange={e => handlePasswordChange(e.target.value)}
                onBlur={() => handleFieldBlur('password')}
                autoComplete="current-password"
                aria-invalid={Boolean(fieldErrors.password)}
              />
              {hasPasswordValue && (
                <TxButton
                  type="button"
                  className="password-visibility-btn"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  onMouseDown={e => e.preventDefault()}
                  onClick={handleTogglePasswordVisibility}
                >
                  <Icon
                    name={showPassword ? 'eyeOff' : 'eye'}
                    className="password-visibility-icon"
                  />
                </TxButton>
              )}
            </div>
          </div>
          {fieldErrors.password && (
            <div className="field-inline-error" role="alert">
              <p className="field-inline-error-message">{t(fieldErrors.password)}</p>
            </div>
          )}
        </div>

        {error && error !== 'otpResendLimitSignInBannerError'  && (
          <TxAlert 
            type={TxAlertType.Error}
            className="sign-in-error-alert"
          >
            {t(error)}
          </TxAlert>
        )}

        <TxButton
          className="btn btn-primary"
          mode={TxButtonMode.Primary}
          type="submit"
          disabled={isLoading}
          aria-busy={isLoading}
        >
          <span className="sign-in-btn-loading">
            {isLoading && <span className="sign-in-spinner" aria-hidden="true" />}
            <Trans file="auth" id="signInNext" />
          </span>
        </TxButton>

        <div className="sign-in-inline-links">
          <span className="sign-in-inline-links-copy">
            <Trans file="auth" id="createAccountPromptPrefix" />
            <TxButton
              type="button"
              className="sign-in-inline-link"
              onClick={handleOpenCreateAccount}
            >
              <span className="sign-in-inline-link-text">
                <Trans file="auth" id="createAccountText" />
              </span>
            </TxButton>
            <Trans file="auth" id="createAccountPromptMid" />
            <TxButton
              type="button"
              className="sign-in-inline-link"
              onClick={handleOpenLearnMore}
            >
              <span className="sign-in-inline-link-text">
                <Trans file="auth" id="learnMoreText" />
              </span>
            </TxButton>
          </span>
          <TxButton
            type="button"
            className="sign-in-recovery-link"
            onClick={() => setShowWhatsWrong(true)}
          >
            <Trans file="auth" id="havingTroubleSigningIn" />
          </TxButton>
        </div>
      </form>
    </div>
  );
};

export default SignInForm;
